<template>
  <div v-if="!!workOrder">
    <div class="row">
      <div class="col-sm-3">
        <h1>{{ workOrder.orderNumber }}</h1>
      </div>
      <div class="col-sm-3 d-none d-lg-block"><h3>Servisni nalog</h3></div>
      <div class="col-sm-3" v-show="!!workOrder.duplicatedFromServiceOrder">
        Podvojen iz
        <button
          class="btn btn-link"
          @click="
            $router.push({ name: 'ServiceOrder', params: { workOrderId: workOrder.duplicatedFromServiceOrderID } })
          "
        >
          {{ workOrder.duplicatedFromServiceOrder }}
        </button>
      </div>
      <div class="col-sm-3 text-end" :class="{ 'offset-md-3': !workOrder.duplicatedFromServiceOrder }">
        <h2>
          <OrderBadge :workOrder="workOrder" />
        </h2>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-12 col-md-4" v-show="isBasedOnOffer">
        <div class="alert alert-warning" role="alert">
          <h5><span v-show="workOrder.isAccepted">✅</span> Nalog po ponudbi!</h5>
        </div>
      </div>

      <div class="col-12 col-md-4" v-show="hasPurchaseOrderRequest">
        <div class="alert alert-info" role="alert">
          <h5><span v-show="workOrder.isAccepted">✅</span> Nalog po naročilnici!</h5>
        </div>
      </div>

      <div class="col-12 col-md-4" v-show="workOrder.plannedHours > 0">
        <div class="alert alert-warning" role="alert">
          <span class="me-2">⚠️</span>
          <strong>Pazi na vgrajen material in porabljen čas!</strong>
          <div class="mt-2">
            <small>Planirano: {{ Math.ceil(workOrder.plannedHours).toFixed(0) }} ur</small>
          </div>
        </div>
      </div>

      <div class="col-12 col-md-4" v-show="workOrder.isGeneratedPeriodic">
        <div class="alert alert-warning" role="alert">
          <h5 v-show="workOrder.isLeakageCheck">Tesnost</h5>
          <h5 v-show="workOrder.isService">Servis</h5>
          <h5 v-show="workOrder.isCondenserCleaning">Čiščenje kondenzatorja (mokro)</h5>
          <h5 v-show="workOrder.isCondenserCleaningDry">Čiščenje kondenzatorja (suho)</h5>
          <h5 v-show="workOrder.isCO2Check">Senzor CO2</h5>
          <h5 v-show="workOrder.isSystemCheck">Pregled sistema</h5>
          <h5 v-show="workOrder.isThermometerCalibration">Umerjanje termometra</h5>
        </div>
      </div>
    </div>

    <div class="row mb-3 mt-3">
      <div class="col-md-8">
        <SelectCustomerEntity
          required
          :customerID="workOrder.customerID"
          :facilityID="workOrder.facilityID"
          :canChange="workOrder.canChange"
          @change="
            (e) => {
              workOrder.customerID = e.customerID
              workOrder.facilityID = e.facilityID
            }
          "
        />

        <div class="row mb-3" v-show="hasProject">
          <label class="col-form-label col-sm-4">Projekt</label>
          <div class="col-sm-8">
            <SelectEntity
              apiEntity="DropDown/Projects"
              v-model="workOrder.projectID"
              :canChange="workOrder.canChange || $hasRoleStartsWith(role, ['Administrator', 'Lead'])"
            />
            <button
              v-if="workOrder.projectID && workOrder.projectID !== $EMPTY_GUID"
              class="btn btn-link"
              @click="$router.push({ name: 'Project', params: { projectId: workOrder.projectID } })"
            >
              Odpri projekt
            </button>
          </div>
        </div>

        <div class="row mb-3">
          <label class="col-form-label col-sm-4">Prijava</label>
          <div class="col-sm-8">
            <input
              class="form-control"
              type="datetime-local"
              v-model="workOrder.dateSubmittedForInput"
              :disabled="!workOrder.canChange"
            />
          </div>
        </div>
        <div class="row mb-3">
          <label class="col-form-label col-sm-4">Delavec</label>
          <div
            :class="{
              'col-sm-8':
                workOrder.statusText !== 'OrderMaterial' || !$hasRoleStartsWith(role, ['Administrator', 'Lead']),
              'col-sm-6':
                workOrder.statusText === 'OrderMaterial' && $hasRoleStartsWith(role, ['Administrator', 'Lead']),
            }"
          >
            <SelectEntity
              apiEntity="DropDown/Workers"
              v-model="workOrder.ownerID"
              :canChange="workOrder.canChange || workOrder.statusText === 'OrderMaterial'"
            />
            <!-- change together with save button -->
          </div>
          <div
            class="col-sm-2"
            v-show="workOrder.statusText === 'OrderMaterial' && $hasRoleStartsWith(role, ['Administrator', 'Lead'])"
          >
            <button class="btn btn-success btn-sm" type="button" @click="saveAsync">💾 Shrani delavca</button>
          </div>
        </div>
        <div class="row mb-3">
          <label class="col-form-label col-sm-4">Zadeva *</label>
          <div class="col-sm-8">
            <input
              class="form-control"
              type="text"
              minlength="4"
              v-model.lazy="workOrder.subject"
              :disabled="!workOrder.canChange"
            />
          </div>
        </div>
        <div class="row mb-3">
          <label class="col-form-label col-sm-4">Opis napake</label>
          <div class="col-sm-8">
            <textarea
              class="form-control"
              v-model="workOrder.issueDescription"
              :disabled="!workOrder.canChange"
              rows="5"
            ></textarea>
          </div>
        </div>
        <div class="row mb-3" v-show="showMessageToWorker">
          <label class="col-form-label col-sm-4">Sporočilo serviserju</label>
          <div class="col-sm-8">
            <textarea class="form-control" v-model="workOrder.message" :disabled="!workOrder.canChange"></textarea>
          </div>
        </div>
        <div class="row mb-3">
          <label class="col-form-label col-sm-4">
            Opis del<br />
            <button
              class="btn btn-outline-danger btn-sm"
              v-show="!!autoSavedWorkDescription && autoSavedWorkDescription !== workOrder.workDescription"
              @click="workOrder.workDescription = autoSavedWorkDescription"
            >
              Povrni prej shranjeni opis del
            </button>
          </label>
          <div class="col-sm-8">
            <textarea
              class="form-control"
              v-model="workOrder.workDescription"
              :disabled="!workOrder.canChange"
              rows="5"
            ></textarea>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-form-label col-3 col-sm-4">Prioriteta *</div>
          <div class="col-9 col-sm-8">
            <IntegerRadioGroup
              v-model="workOrder.priority"
              :canChange="workOrder.canChange"
              :numberOfValues="3"
              namePrefix="priority"
              :valueNames="['1', '2', '3 (najnižja)']"
            />
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-form-label col-3 col-sm-4">Potrebuje dva delavca:</div>
          <div class="col-9 col-sm-8">
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                id="is-active"
                v-model="workOrder.needsTwo"
                :disabled="
                  !(
                    workOrder.isNew ||
                    ($hasRoleStartsWith(role, ['Leader', 'Admin', 'WorkerService']) &&
                      $hasStatus(workOrder, ['Triage', 'Active', 'OrderMaterial', 'MaterialOrdered', 'Offer']))
                  )
                "
              />
            </div>
          </div>
        </div>

        <div class="row mb-3" v-show="$hasRoleStartsWith(role, ['Leader', 'Admin'])">
          <label class="col-form-label col-sm-4">Skladišče za komisioniranje</label>
          <div class="col-sm-8">
            <SelectEntity
              apiEntity="DropDown/Warehouses"
              v-model="workOrder.commissionWarehouseId"
              :canChange="workOrder.canChangeCommissionWarehouse"
              @selected="unsavedChangesToCommissionWarehouse = true"
            />
          </div>
        </div>
        <div class="row mb-3">
          <label class="col-form-label col-sm-4">Planiran datum</label>
          <div class="col-sm-4">
            <input
              class="form-control"
              type="date"
              v-model="workOrder.datePlannedVisitForInput"
              :disabled="!workOrder.canChange"
            />
          </div>
          <div class="col-sm-4">
            <button
              class="btn btn-primary btn-sm"
              type="button"
              @click="sendEmailAsync('plannedVisit')"
              v-show="workOrder.canChange && !workOrder.isNew && workOrder.facility?.offerRecipients?.length > 0"
              :disabled="!isValid || !workOrder.datePlannedVisit || hasUnsavedChanges"
            >
              ✉ Obvesti stranko (datum)
            </button>
            <span>
              <a
                href="#"
                v-for="d in $createQuickDates(0, 5)"
                :key="d.label"
                class="me-2"
                @click.prevent="setPlannedVisit(d.date)"
                >{{ d.label }}</a
              >
            </span>
          </div>
        </div>

        <div class="row mb-3" v-if="$hasRoleStartsWith(role, ['Leader', 'Admin'])">
          <label class="col-form-label col-sm-4">Ocena dela</label>
          <div class="col-sm-5">
            <input
              class="form-control"
              type="time"
              v-model="workOrder.workAssessment"
              :disabled="!workOrder.canChange"
            />
          </div>
        </div>

        <div
          class="fire-watch"
          v-show="
            $hasRoleStartsWith(role, ['Leader', 'Admin', 'WorkerService']) &&
            $hasStatus(workOrder, ['Triage', 'Accepted', 'Active', 'InProgress']) &&
            workOrder.isFireWatch
          "
        >
          <div class="alert alert-success" v-show="workOrder.fireWatch.hasAllSignoffs" role="alert">
            <strong>✅ Zbrani vsi podpisi.</strong>
          </div>

          <div class="row mb-3">
            <label class="col-form-label col-sm-4">Izvajalec požarne straže (1.4)</label>
            <div class="col-sm-8">
              <input
                class="form-control"
                type="text"
                v-model="workOrder.fireWatch.fireWatchOperator"
                :disabled="!workOrder.canChange"
              />
            </div>
          </div>

          <div class="row mb-3">
            <label class="col-form-label col-sm-4">Veljavnost dovoljenja od:</label>
            <div class="col-sm-8">
              <input
                class="form-control"
                type="datetime-local"
                v-model="workOrder.fireWatch.validFromForInput"
                :disabled="!workOrder.canChange"
              />
            </div>
          </div>
          <div class="row mb-3">
            <label class="col-form-label col-sm-4">Veljavnost dovoljenja do:</label>
            <div class="col-sm-8">
              <input
                class="form-control"
                type="datetime-local"
                v-model="workOrder.fireWatch.validToForInput"
                :disabled="!workOrder.canChange"
              />
            </div>
          </div>

          <div class="row mb-3">
            <label class="col-form-label col-sm-4">Požarna straža - Točna lokacija</label>
            <div class="col-sm-8">
              <input
                class="form-control"
                type="text"
                v-model="workOrder.fireWatch.exactLocation"
                :disabled="!workOrder.canChange"
              />
            </div>
          </div>
          <div class="row mb-3">
            <label class="col-form-label col-sm-4">Posebnosti pred začetkom dela</label>
            <div class="col-sm-8">
              <input
                class="form-control"
                type="text"
                v-model="workOrder.fireWatch.extraWork"
                :disabled="!workOrder.canChange"
              />
            </div>
          </div>
          <div class="row mb-3">
            <label class="col-form-label col-sm-4">Požarna straža - Potrebni gasilci</label>
            <div class="col-sm-8">
              <input
                class="form-check-input"
                type="checkbox"
                id="fire-watch-require-firefighters"
                v-model="workOrder.fireWatch.requireFirefighters"
                :disabled="!workOrder.canChange"
              />
              <label class="form-check-label ms-2" for="fire-watch-require-firefighters">Da</label>
            </div>
          </div>
          <div class="row mb-3">
            <label class="col-form-label col-sm-4">Izvajalec požarne straže</label>
            <div class="col-sm-8">
              <div class="row g-2">
                <div class="col-12 col-md-5">
                  <input
                    class="form-control"
                    type="text"
                    v-model="workOrder.fireWatch.attendant"
                    placeholder="Ime in priimek"
                    :disabled="!workOrder.canChange || workOrder.fireWatch.attendantApproval"
                  />
                </div>
                <div class="col-12 col-md-2">
                  <button
                    class="btn btn-sm btn-outline-primary w-100"
                    v-show="!workOrder.fireWatch.attendantApproval"
                    @click="
                      signDocumentAsync('hotWorkPermit', {
                        recipientType: 'attendant',
                        signatureName: workOrder.fireWatch.attendant,
                      })
                    "
                    :disabled="workOrder.isNew || hasUnsavedChanges || !workOrder.fireWatch.attendant"
                  >
                    Podpiši
                  </button>
                  <span v-show="workOrder.fireWatch.attendantApproval" class="badge bg-success w-100 py-2">✅</span>
                </div>
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <label class="col-form-label col-sm-4">Izvajalec vročih del</label>
            <div class="col-sm-8">
              <div class="row g-2">
                <div class="col-12 col-md-5">
                  <input
                    class="form-control"
                    type="text"
                    v-model="workOrder.fireWatch.hotWorkOperator"
                    placeholder="Ime in priimek"
                    :disabled="!workOrder.canChange || workOrder.fireWatch.hotWorkOperatorApproval"
                  />
                </div>
                <div class="col-12 col-md-2">
                  <button
                    class="btn btn-sm btn-outline-primary w-100"
                    v-show="!workOrder.fireWatch.hotWorkOperatorApproval"
                    @click="
                      signDocumentAsync('hotWorkPermit', {
                        recipientType: 'hotWorkOperator',
                        signatureName: workOrder.fireWatch.hotWorkOperator,
                      })
                    "
                    :disabled="workOrder.isNew || hasUnsavedChanges || !workOrder.fireWatch.hotWorkOperator"
                  >
                    Podpiši
                  </button>
                  <span v-show="workOrder.fireWatch.hotWorkOperatorApproval" class="badge bg-success w-100 py-2"
                    >✅</span
                  >
                </div>
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <label class="col-form-label col-sm-4">Naročnik področni</label>
            <div class="col-sm-8">
              <div class="row g-2">
                <div class="col-12 col-md-5">
                  <input
                    class="form-control"
                    type="text"
                    v-model="workOrder.fireWatch.authorizingOfficer"
                    placeholder="Ime in priimek"
                    :disabled="!workOrder.canChange || workOrder.fireWatch.authorizingOfficerApproval"
                  />
                </div>
                <div class="col-12 col-md-5">
                  <input
                    class="form-control"
                    type="email"
                    v-model="workOrder.fireWatch.authorizingOfficerEmail"
                    placeholder="Email"
                    :disabled="!workOrder.canChange || workOrder.fireWatch.authorizingOfficerApproval"
                  />
                </div>
                <div class="col-12 col-md-2">
                  <button
                    class="btn btn-sm btn-outline-primary w-100"
                    v-show="!workOrder.fireWatch.authorizingOfficerApproval"
                    @click="sendEmailAsync('hotWorkPermit', { recipientType: 'authorizingOfficer' })"
                    :disabled="workOrder.isNew || !workOrder.fireWatch.authorizingOfficerEmail || hasUnsavedChanges"
                  >
                    Pošlji
                    <span v-show="workOrder.fireWatch.authorizingOfficerEmailSent">ponovno</span>
                    ✉
                  </button>
                  <span v-show="workOrder.fireWatch.authorizingOfficerApproval" class="badge bg-success w-100 py-2"
                    >✅</span
                  >
                </div>
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <label class="col-form-label col-sm-4">Naročnik poslovodja</label>
            <div class="col-sm-8">
              <div class="row g-2">
                <div class="col-12 col-md-5">
                  <input
                    class="form-control"
                    type="text"
                    v-model="workOrder.fireWatch.departmentManager"
                    placeholder="Ime in priimek"
                    :disabled="!workOrder.canChange || workOrder.fireWatch.departmentManagerApproval"
                  />
                </div>
                <div class="col-12 col-md-5">
                  <input
                    class="form-control"
                    type="email"
                    v-model="workOrder.fireWatch.departmentManagerEmail"
                    placeholder="Email"
                    :disabled="!workOrder.canChange || workOrder.fireWatch.departmentManagerApproval"
                  />
                </div>
                <div class="col-12 col-md-2">
                  <button
                    class="btn btn-sm btn-outline-primary w-100"
                    v-show="!workOrder.fireWatch.departmentManagerApproval"
                    @click="sendEmailAsync('hotWorkPermit', { recipientType: 'departmentManager' })"
                    :disabled="workOrder.isNew || !workOrder.fireWatch.departmentManagerEmail || hasUnsavedChanges"
                  >
                    Pošlji
                    <span v-show="workOrder.fireWatch.departmentManagerEmailSent">ponovno</span>
                    ✉
                  </button>
                  <span v-show="workOrder.fireWatch.departmentManagerApproval" class="badge bg-success w-100 py-2"
                    >✅</span
                  >
                </div>
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <label class="col-form-label col-sm-4">Upravnik stavbe</label>
            <div class="col-sm-8">
              <div class="row g-2">
                <div class="col-12 col-md-5">
                  <input
                    class="form-control"
                    type="text"
                    v-model="workOrder.fireWatch.externalManager"
                    placeholder="Ime in priimek"
                    :disabled="!workOrder.canChange || workOrder.fireWatch.externalManagerApproval"
                  />
                </div>
                <div class="col-12 col-md-5">
                  <input
                    class="form-control"
                    type="email"
                    v-model="workOrder.fireWatch.externalManagerEmail"
                    placeholder="Email"
                    :disabled="!workOrder.canChange || workOrder.fireWatch.externalManagerApproval"
                  />
                </div>
                <div class="col-12 col-md-2">
                  <button
                    class="btn btn-sm btn-outline-primary w-100"
                    v-show="!workOrder.fireWatch.externalManagerApproval"
                    @click="sendEmailAsync('hotWorkPermit', { recipientType: 'externalManager' })"
                    :disabled="workOrder.isNew || !workOrder.fireWatch.externalManagerEmail || hasUnsavedChanges"
                  >
                    Pošlji
                    <span v-show="workOrder.fireWatch.externalManagerEmailSent">ponovno</span>
                    ✉
                  </button>
                  <span v-show="workOrder.fireWatch.externalManagerApproval" class="badge bg-success w-100 py-2"
                    >✅</span
                  >
                </div>
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <label class="col-form-label col-sm-4">več kot 100 ljudi v objektu</label>
            <div class="col-sm-8">
              <input
                class="form-check-input"
                type="checkbox"
                id="fire-watch-more-than-100"
                v-model="workOrder.fireWatch.moreThan100"
                :disabled="!workOrder.canChange"
              />
              <label class="form-check-label ms-2" for="fire-watch-more-than-100">Da, več kot 100</label>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-form-label col-3 col-sm-4">Ocena ogroženosti</div>
            <div class="col-9 col-sm-8">
              <IntegerRadioGroup
                v-model="workOrder.fireWatch.level"
                :canChange="workOrder.canChange"
                :numberOfValues="6"
                namePrefix="fireWatchLevel"
              />
            </div>
          </div>
        </div>

        <div
          v-show="$hasRoleStartsWith(role, ['Leader', 'Admin']) && $hasStatus(workOrder, ['Completed', 'Finalized'])"
        >
          <div class="row mb-3">
            <div class="col-form-label col-3 col-sm-4">Težavnost izvedbe</div>
            <div class="col-9 col-sm-8">
              <IntegerRadioGroup v-model="workOrder.difficultyLevel" :numberOfValues="5" namePrefix="difficultyLevel" />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-form-label col-3 col-sm-4">Kvaliteta izvedbe</div>
            <div class="col-9 col-sm-8">
              <IntegerRadioGroup v-model="workOrder.qualityLevel" :numberOfValues="5" namePrefix="qualityLevel" />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-form-label col-3 col-sm-4">Pripravljenost na delo izven delovnega časa</div>
            <div class="col-9 col-sm-8">
              <IntegerRadioGroup
                v-model="workOrder.extendedHoursLevel"
                :numberOfValues="2"
                namePrefix="extendedHoursLevel"
                :valueNames="['NE', 'DA']"
              />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-form-label col-3 col-sm-4">Skrb za minimalno zalogo</div>
            <div class="col-9 col-sm-8">
              <IntegerRadioGroup
                v-model="workOrder.minStockCareLevel"
                :numberOfValues="2"
                namePrefix="minStockCare"
                :valueNames="['NE', 'DA']"
              />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-form-label col-3 col-sm-4">Popolni popis</div>
            <div class="col-9 col-sm-8">
              <IntegerRadioGroup
                v-model="workOrder.fullListLevel"
                :numberOfValues="2"
                namePrefix="fullList"
                :valueNames="['NE', 'DA']"
              />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-form-label col-3 col-sm-4">Mentorstvo</div>
            <div class="col-9 col-sm-8">
              <IntegerRadioGroup
                v-model="workOrder.mentoringLevel"
                :numberOfValues="2"
                namePrefix="mentoring"
                :valueNames="['NE', 'DA']"
              />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-form-label col-3 col-sm-4">Nalog je potreboval sanacijo</div>
            <div class="col-9 col-sm-8">
              <div class="d-flex align-items-center">
                <IntegerRadioGroup
                  v-model="remediationStatus"
                  :numberOfValues="2"
                  namePrefix="remediation"
                  :valueNames="['NE', 'DA']"
                  @update:modelValue="handleRemediationChange"
                />
                <span v-if="workOrder.dateRemediation" class="ms-3">
                  {{ $dayjs(workOrder.dateRemediation).format($DATE_FORMAT_DISPLAY) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card mb-3">
          <div class="card-body">
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                id="is-special"
                v-model="workOrder.isSpecial"
                :disabled="
                  !workOrder.canChange || !(workOrder.isNew || $hasRoleStartsWith(role, ['Administrator', 'Lead']))
                "
              />
              <label class="form-check-label" for="is-special">Ima posebnosti</label>
            </div>
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                id="is-fire-watch"
                v-model="workOrder.isFireWatch"
                @change="handleFireWatchToggle"
                :disabled="
                  !workOrder.canChange ||
                  !(workOrder.isNew || $hasRoleStartsWith(role, ['Administrator', 'Lead', 'WorkerService'])) ||
                  !$hasStatus(workOrder, ['Triage', 'Accepted', 'Active', 'InProgress'])
                "
              />
              <label class="form-check-label" for="is-fire-watch">Ima požarno stražo</label>
            </div>
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                id="requires-attention"
                v-model="workOrder.isAttention"
                :disabled="
                  !workOrder.canChange || !(workOrder.isNew || $hasRoleStartsWith(role, ['Administrator', 'Lead']))
                "
              />
              <label class="form-check-label" for="requires-attention">Zahteva pozornost</label>
            </div>
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                id="has-warranty"
                v-model="workOrder.hasWarranty"
                :disabled="
                  !workOrder.canChange || (!workOrder.canChange && !$hasRoleStartsWith(role, ['Administrator', 'Lead']))
                "
              />
              <label class="form-check-label" for="has-warranty">Garancija</label>
            </div>
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                id="has-project"
                @input="(e) => handleHasProject(e.target.checked)"
                :checked="hasProject"
                :disabled="
                  !workOrder.canChange || (!workOrder.canChange && !$hasRoleStartsWith(role, ['Administrator', 'Lead']))
                "
              />
              <label class="form-check-label" for="has-project">Del projekta</label>
            </div>
            <p class="card-text" v-show="workOrder.hadUncompletedCommission">
              <span class="badge bg-warning text-dark">Delni komision!</span>
            </p>
            <p class="card-text" v-show="facility">
              <button class="btn btn-sm btn-primary" @click="showFacilityHistory = true">Zgodovina obiskov</button>
            </p>
          </div>
        </div>

        <div
          class="card-body card mb-3"
          v-show="workOrder.offers.length > 0"
          v-if="$hasRoleStartsWith(role, ['Leader', 'Admin'])"
        >
          <h5 class="card-title">Ponudbe in naročilnice na nalogu</h5>
          <ul class="list-unstyled">
            <li v-for="o in workOrder.offers" :key="o.id" class="mb-2">
              <div v-if="o.isLatest" class="d-flex align-items-center bg-light border border-primary rounded p-2">
                <div>
                  <span v-if="o.attachmentID === $EMPTY_GUID">{{ o.number }} (zahtevek za naročilnico)</span>
                  <a
                    v-else
                    :href="$AZURE_URI + '/Attachment/' + o.attachmentID"
                    target="_blank"
                    class="h6 mb-0 d-inline-block text-decoration-none"
                    >{{ o.number }}</a
                  >
                  <div class="small text-muted">{{ $dayjs(o.dateCreated).format($DATE_FORMAT_DISPLAY) }}</div>
                  <router-link :to="{ name: 'OfferEdit', params: { offerId: o.id } }">Urejaj</router-link>
                </div>
                <div class="ms-auto d-flex align-items-center">
                  <span v-if="o.status === 'Sent'" class="me-2 fs-5">📨</span>
                  <span v-else-if="o.status === 'Declined'" class="me-2 fs-5">❌</span>
                  <span v-else-if="o.status === 'Accepted'" class="me-2 fs-5">✅</span>
                  <span v-else-if="o.status === 'Issued'" class="badge me-2">Izdana</span>
                  <div class="btn-group" v-if="o.status !== 'Accepted'">
                    <button
                      class="btn btn-sm btn-primary"
                      :disabled="!workOrder.canSendOffer || hasUnsavedChanges"
                      @click="sendOfferAsync(o)"
                    >
                      📨 Pošlji
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="d-flex align-items-center px-2">
                <a
                  :href="$AZURE_URI + '/Attachment/' + o.attachmentID"
                  target="_blank"
                  class="me-2 text-decoration-none"
                  >{{ o.number }}</a
                >
                <small class="text-muted me-2">{{ $dayjs(o.dateCreated).format($DATE_FORMAT_DISPLAY) }}</small>
                <div class="ms-auto">
                  <span v-if="o.status === 'Declined'">❌</span>
                  <span v-else-if="o.status === 'Accepted'">✅</span>
                  <span v-else-if="o.status === 'Sent'">📨</span>
                  <span v-else-if="o.status === 'Issued'" class="badge">Izdana</span>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <ServiceOrderCallsCard v-if="!workOrder.isNew" class="card mb-3" :serviceOrderId="workOrder.id" />

        <ServiceOrdersCard
          v-if="!workOrder.isNew && !!workOrder.facilityID && workOrder.facilityID !== $EMPTY_GUID"
          class="card mb-3"
          title="Ponudbe"
          subTitle="za trenutni objekt"
          statuses="Offer,Declined"
          :itemsPerPage="20"
          :showLinkToMap="false"
          :facilityID="workOrder.facilityID"
          :showFacility="false"
          :showWhenEmpty="false"
        />

        <ServiceOrdersCard
          v-if="!workOrder.isNew && !!workOrder.facilityID && workOrder.facilityID !== $EMPTY_GUID"
          class="card mb-3"
          title="Servisni nalogi"
          subTitle="Active,Accepted,InProgress,Triage + trenutni objekt"
          statuses="Active,Accepted,InProgress,Triage"
          :itemsPerPage="20"
          :showLinkToMap="false"
          :facilityID="workOrder.facilityID"
          :actionTitle="isAdmin && workOrder.ownerID && workOrder.ownerID !== $EMPTY_GUID ? 'Dodeli' : ''"
          @select="assignOwnerAsync"
          :excludeId="workOrder.id"
          :showFacility="false"
          :showOwner="true"
          ref="serviceOrdersCardEl"
        />

        <div
          class="card mb-3"
          v-if="!isNew && (!!devices?.find((d) => d.isLeakageTest) || workOrder.hasLeakageCheckForms)"
        >
          <div class="card-body">
            <h5 class="card-title d-none d-lg-block">Uhajanje</h5>

            <p class="card-text mt-3" v-show="workOrder?.leakageCheckForms?.length > 0">
              {{ workOrder.leakageCheckForms.length }} preskusi tesnosti od
              {{ devices?.filter((d) => d.isLeakageTest).length }} naprav:
            </p>
            <p class="card-text mt-3" v-show="workOrder?.leakageCheckForms?.length === 0">
              Št. naprav: {{ devices?.filter((d) => d.isLeakageTest).length }}.
            </p>
            <ul>
              <li v-for="f in workOrder.leakageCheckForms">
                <a :href="$AZURE_URI + '/Generated/Leakage/' + workOrder.id + '?leakageFormId=' + f.id">{{
                  f.device?.label
                }}</a>
              </li>
            </ul>
          </div>
        </div>

        <div class="card mb-3" v-if="workOrder.hasChecklists">
          <div class="card-body">
            <h5 class="card-title d-none d-lg-block">Kontrolni listi</h5>
            <ul>
              <li
                v-for="cr in workOrder.checklists.filter((c) => c.type === 'CalibrationReport')"
                role="button"
                class="text-decoration-underline"
              >
                <a
                  v-if="workOrder.ownerID !== $EMPTY_GUID"
                  :href="$AZURE_URI + '/Generated/CalibrationReport/' + workOrder.id + '?deviceId=' + cr.device.id"
                  target="_blank"
                >
                  {{ cr.device.label }} {{ cr.device.deviceNumber }}
                </a>
                <span v-else>{{ cr.device.label }} - {{ cr.device.deviceNumber }} (izberi delavca za podpis)</span>
              </li>
              <li
                v-for="cr in workOrder.checklists.filter((c) => c.type === 'SystemReport')"
                role="button"
                class="text-decoration-underline"
              >
                <a
                  :href="$AZURE_URI + '/Generated/SystemReport/' + workOrder.id + '?deviceId=' + cr.device.id"
                  target="_blank"
                >
                  Kontrola delovanja hladilnega sistema
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div class="card mb-3" v-show="workOrder.warehouseTransfers?.length">
          <div class="card-body">
            <h5 class="card-title d-none d-lg-block">Medskladiščnice</h5>
            <ul>
              <li v-for="wt in workOrder.warehouseTransfers">
                <router-link
                  :to="{ name: 'WarehouseTransferEdit', params: { id: wt.id, type: wt.typeText } }"
                  target="_blank"
                  >{{ wt.number }}</router-link
                >
                &nbsp;
                <OrderBadge :workOrder="wt" />
                {{ wt.to?.name }}
              </li>
            </ul>
            <p
              class="card-text"
              v-show="
                $hasRoleStartsWith(role, ['Leader', 'Admin']) &&
                ['Triage', 'Active', 'Accepted', 'OrderMaterial', 'MaterialOrdered'].includes(workOrder.statusText)
              "
            >
              <button class="btn btn-info" @click="clearCommissionsAsync">Resetiraj komisione</button>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-3">
      <div class="h-100 p-4 bg-light border rounded-3 mb-3" v-if="workOrder.status > 2">
        <WorkOrderTransports
          :canChange="workOrder.canChange"
          work-order-type="Service"
          :workOrderId="workOrder.id"
          :defaultStartTime="defaults.timeEndTime"
          @finished="
            (e) => {
              $log('finishedTransport', e)
              defaults.transportEndTime = e
            }
          "
        />
      </div>

      <div class="h-100 p-4 bg-light border rounded-3 mb-3" v-if="workOrder.status > 2">
        <WorkOrderTimes
          :canChange="workOrder.canChange"
          work-order-type="Service"
          :workOrderId="workOrder.id"
          :defaultStartTime="defaults.transportEndTime"
          @finished="
            (e) => {
              $log('finishedTime', e)
              defaults.timeEndTime = e
            }
          "
          :addMinutes="10"
        />
      </div>

      <div
        class="h-100 p-4 bg-light border rounded-3 mb-3"
        v-if="
          !workOrder.isNew && !unsavedChangesToCommissionWarehouse && workOrder.commissionWarehouseId !== $EMPTY_GUID
        "
      >
        <WorkOrderMaterials
          ref="workOrderMaterialsEl"
          :canChange="workOrder.canChangeMaterials"
          :showCommission="
            $hasRoleStartsWith(role, ['Administrator', 'Lead']) &&
            ['Triage', 'OrderMaterial', 'MaterialOrdered'].includes(workOrder.statusText)
          "
          :showMissingCommission="$hasRoleStartsWith(role, ['Administrator', 'Lead'])"
          :disableCommission="workOrder.commissionWarehouseId === $EMPTY_GUID"
          work-order-type="ServiceOrder"
          :workOrderId="workOrder.id"
          :showOrderMaterial="
            workOrder.statusText === 'OrderMaterial' && $hasRoleStartsWith(role, ['Administrator', 'Lead'])
          "
          :showStock="
            $hasRoleStartsWith(role, ['Administrator', 'Lead']) &&
            ['Triage', 'OrderMaterial', 'MaterialOrdered'].includes(workOrder.statusText)
          "
          :serialsRequired="
            !['New', 'Triage', 'Active', 'Accepted', 'OrderMaterial', 'MaterialOrdered'].includes(workOrder.statusText)
          "
          @commissioned="workOrder.canChangeCommissionWarehouse = false"
          :canDeleteMaterial="workOrder.canDeleteMaterials"
          :roundUp="true"
          :enableTemplates="true"
        />
        <div class="row">
          <div class="col">
            <h6>Dodatni material</h6>
            <textarea
              class="form-control"
              placeholder="dodatno nabavljeni material (ni v šifrantu)"
              v-model="workOrder.additionalMaterial"
              :disabled="!workOrder.canChange"
            ></textarea>
          </div>
        </div>
      </div>

      <div class="h-100 p-4 bg-light border rounded-3 mb-3" v-if="!workOrder.isNew && isValid">
        <div class="row">
          <h4 class="col-2">Naprave</h4>
          <div class="col-2">
            <button
              class="btn btn-link"
              type="button"
              @click="
                $router.push({
                  name: 'DeviceEdit',
                  params: { deviceId: 'new', customerId: workOrder.customerID, facilityId: workOrder.facilityID },
                })
              "
            >
              Nova naprava
            </button>
          </div>
          <div class="col-5 text-end">
            <button
              class="btn btn-success btn-sm me-2 mb-2"
              @click="saveAndOpenNewLeakCheckFormAsync"
              :disabled="
                ['Triage', 'Active', 'MaterialOrdered', 'PurchaseOrderAccepted', 'Declined', 'Deleted'].includes(
                  workOrder.statusText,
                ) || !isValid
              "
              v-show="workOrder.canChange || $hasRoleStartsWith(role, ['Administrator', 'Lead'])"
            >
              💾 <span class="d-none d-lg-inline">Odpri obrazec za</span> preskus tesnosti
            </button>
            <button
              type="button"
              class="btn btn-secondary btn-sm me-2 mb-2"
              @click="showBarcode = true"
              v-show="workOrder.canChange"
            >
              <span class="d-none d-lg-inline">Bralnik </span>črtne kode
            </button>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <DeviceList
              :serviceOrderId="workOrder.id"
              :devices="devices"
              :showService="true"
              :showFacility="false"
              :showSelect="true"
              :isService="workOrder.isService"
              :isCondenserCleaning="workOrder.isCondenserCleaning"
              :isCondenserCleaningDry="workOrder.isCondenserCleaningDry"
              :isThermometerCalibration="workOrder.isThermometerCalibration"
              :isSystemCheck="workOrder.isSystemCheck"
              :isLeakageCheck="workOrder.isLeakageCheck"
              :isCo2Check="workOrder.isCO2Check"
              :enableDepth="true"
            />
          </div>
        </div>
      </div>
      <SelectDevice
        :visible="showBarcode"
        :facilityId="workOrder.facilityID"
        @add="addDeviceToServiceOrder"
        @close="showBarcode = false"
      />

      <div class="h-100 p-4 bg-light border rounded-3 mb-3" v-if="!workOrder.isNew">
        <div class="row">
          <h4 class="col-2">Priloge</h4>
          <div class="col-10 text-end">
            <a
              v-show="workOrder.statusText === 'Completed' || workOrder.statusText === 'Finalized'"
              :href="$AZURE_URI + '/Generated/ServiceOrder/' + workOrder.id"
              target="_blank"
              class="me-2"
              >PDF preview</a
            >
            <button
              class="btn btn-warning btn-sm ms-1 me-2 mb-2"
              @click="showUpload = true"
              v-show="workOrder.canChange"
            >
              Naloži datoteko
            </button>
            <button
              class="btn btn-secondary btn-sm ms-1 me-2 mb-2"
              @click="showAttachFromCamera = true"
              v-show="workOrder.canChange"
            >
              Naloži fotografijo
            </button>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <Attachments
              :attachments="attachments"
              :showDelete="$hasRole(role, ['LeaderService', 'Administrator']) || workOrder?.status < 6"
              @delete="
                (e) => {
                  deleteAttachment(e)
                }
              "
            />
          </div>
        </div>
      </div>
    </div>

    <RequiredWarning v-show="!isValid && workOrder.canChange" />

    <div class="alert alert-warning" role="alert" v-show="!areLeakageCheckFormsOk">
      Izpolni obrazce za preskus tesnosti!
    </div>

    <div
      class="row mb-5 py-2 bg-light"
      style="position: sticky; bottom: 0; z-index: 1020; border-top: 4px solid #dee2e6"
    >
      <div class="col-2">
        <button
          v-show="!isNew && (workOrder.canDelete || $hasRoleStartsWith(role, ['Administrator', 'Lead']))"
          class="btn btn-danger btn-sm me-3 mb-2"
          type="button"
          @click="deleteOrderAsync"
        >
          🗑️ Izbriši
        </button>
      </div>
      <div class="col-10 text-end">
        <button
          class="btn btn-outline-secondary btn-sm me-3 mb-2"
          type="button"
          @click="$router.push({ name: 'Service' })"
        >
          ✖️ Prekliči
        </button>

        <button
          class="btn btn-success btn-sm me-3 mb-2"
          type="button"
          v-show="workOrder.statusText === 'Offer' && $hasRoleStartsWith(role, ['Administrator', 'Lead'])"
          :disabled="!isValid || hasUnsavedChanges || workOrder.offers?.length > 0"
          @click="saveOfferAsync(false)"
        >
          🧾 Ponudba
        </button>

        <button
          class="btn btn-success btn-sm me-3 mb-2"
          type="button"
          v-show="workOrder.statusText === 'Triage' && $hasRoleStartsWith(role, ['Administrator', 'Lead'])"
          :disabled="!isValid || !workOrder.canSendOffer || hasUnsavedChanges"
          @click="saveOfferAsync(true)"
        >
          📩 📨 Naročilnica
        </button>

        <button
          class="btn btn-info btn-sm me-3 mb-2"
          type="button"
          v-show="
            ['Triage', 'Active', 'MaterialOrdered'].includes(workOrder.statusText) &&
            $hasRoleStartsWith(role, ['Administrator', 'Lead'])
          "
          :disabled="workOrder.commissionWarehouseId === $EMPTY_GUID"
          @click="
            () => {
              unsavedChangesToCommissionWarehouse = false
              verifyStockOrderAsync()
            }
          "
        >
          💾 🛒 Naroči mat.
        </button>

        <button
          class="btn btn-outline-primary btn-sm me-3 mb-2"
          type="button"
          v-show="workOrder.statusText === 'Completed'"
          @click="$router.push({ name: 'SignOrder', params: { workOrderId: workOrder.id } })"
        >
          📧 Ponovno pošlji email
        </button>

        <button
          class="btn btn-success btn-sm me-3 mb-2"
          type="button"
          v-show="workOrder.canComplete"
          :disabled="
            hasUnsavedChanges ||
            hasOpenWarehouseTransfers ||
            (workOrder.isFireWatch && !workOrder.fireWatch.hasAllSignoffs) ||
            !areLeakageCheckFormsOk
          "
          @click="
            $router.push({ name: 'FacilityServiceOrders', params: { workOrderId: workOrder.id, keepMessages: true } })
          "
        >
          ✍️ Podpis stranke
          <span v-if="hasOpenWarehouseTransfers"> ⏳</span>
          <span v-if="workOrder.isFireWatch && !workOrder.fireWatch.hasAllSignoffs"> 🔒</span>
          <span v-if="!areLeakageCheckFormsOk"> ⚠️</span>
        </button>

        <button
          class="btn btn-success btn-sm me-3 mb-2"
          type="button"
          v-show="
            $hasRoleStartsWith(role, ['Administrator', 'Lead']) &&
            ['Active', 'OrderMaterial', 'InProgress'].includes(workOrder.statusText)
          "
          :disabled="hasUnsavedChanges"
          @click="markTriageAsync"
        >
          ⚑ V triažo
        </button>

        <button
          class="btn btn-success btn-sm me-3 mb-2"
          type="button"
          v-show="
            ['Triage', 'MaterialOrdered', 'Offer', 'OrderMaterial'].includes(workOrder.statusText) &&
            $hasRoleStartsWith(role, ['Administrator', 'Lead'])
          "
          :disabled="hasUnsavedChanges"
          @click="markActiveAsync"
        >
          ✅ V aktivni
        </button>

        <button
          class="btn btn-success btn-sm me-3 mb-2"
          type="button"
          v-show="
            ['New', 'Triage', 'Active', 'OrderMaterial'].includes(workOrder.statusText) &&
            $hasRoleStartsWith(role, ['Administrator', 'Lead'])
          "
          :disabled="hasUnsavedChanges"
          @click="markOfferAsync"
        >
          🧾 V ponudbo
        </button>

        <button
          class="btn btn-outline-danger btn-sm me-3 mb-2"
          type="button"
          v-show="
            [
              'Triage',
              'OrderMaterial',
              'MaterialOrdered',
              'Completed',
              'Finalized',
              'Deleted',
              'Declined',
              'PurchaseOrderAccepted',
            ].includes(workOrder.statusText)
          "
          :disabled="!isValid || !$hasRoleStartsWith(role, ['Administrator', 'Lead'])"
          @click="saveAsNewAsync"
        >
          💾 📄 Podvoji DN
        </button>

        <button
          class="btn btn-primary btn-lg me-3 mb-2"
          type="button"
          :disabled="
            !isValid ||
            (!workOrder.canChange && !$hasRoleStartsWith(role, ['Administrator', 'Lead'])) ||
            ['Deleted', 'Declined', 'PurchaseOrderAccepted'].includes(workOrder.statusText)
          "
          @click="saveAsync"
        >
          <span v-if="hasUnsavedChanges" class="blink-save-icon">💾</span>
          Shrani
        </button>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <div class="alert alert-warning" role="alert" v-show="workOrder.commissionWarehouseId === $EMPTY_GUID">
          Skladišče za komisioniranje ni določeno.
        </div>
      </div>
    </div>

    <ChangeHistory v-model="workOrder" v-show="!isNew" />
    <FacilityHistory :facilityId="facility?.id" @close="showFacilityHistory = false" :visible="showFacilityHistory" />
    <Upload
      :visible="!!showUpload"
      @close="showUpload = false"
      @add="
        (e) => {
          addAttachment(e)
        }
      "
      type="ServiceOrder"
      :id="workOrder.id"
    />
    <AttachFromCamera
      :visible="!!showAttachFromCamera"
      @close="showAttachFromCamera = false"
      @add="
        (e) => {
          addAttachment(e)
        }
      "
      type="ServiceOrder"
      attachmentType="DeviceState"
      :id="workOrder.id"
    />
  </div>
</template>

<script setup>
import { ref, toRef, toRefs, computed, defineComponent, watch, reactive, onMounted } from "vue"
import { useWorkOrder } from "../composition-api/useWorkOrder"
import {
  useAuth,
  authState,
  isAuthenticated,
  user,
  role,
  isAdmin,
  showMessageToWorker,
} from "../composition-api/useAuth"
import { getFacilityAsync } from "../api"
import { EMPTY_GUID } from "scripts/utils/constants"
import AttachFromCamera from "../components/AttachFromCamera.vue"
import FacilityHistory from "../components/FacilityHistory.vue"
import DeviceList from "../components/DeviceList.vue"
import SelectDevice from "../components/SelectDevice.vue"
import ServiceOrdersCard from "../components/ServiceOrdersCard.vue"
import ServiceOrderCallsCard from "../components/ServiceOrderCallsCard.vue"

defineOptions({ name: "ServiceOrder" })

const {
  isNew,
  workOrder,
  hasUnsavedChanges,
  saveWorkOrderAsync,
  saveAsNewWorkOrderAsync,
  deleteOrderAsync,
  attachments,
  devices,
  addAttachment,
  deleteAttachment,
  verifyStockOrderAsync,
  changeOwnerAsync,
  clearCommissionsAsync,
  changeWorkOrderStatusAsync,
  runOfferAsync,
  loadWorkOrderAsync,
  sendEmailAsync,
} = useWorkOrder("Service")

const serviceOrdersCardEl = ref(null)
const workOrderMaterialsEl = ref(null)

const assignOwnerAsync = async (wo) => {
  if (workOrder.value.ownerID === EMPTY_GUID || !workOrder.value.ownerID) {
    console.log("nobody to assign to")
    return
  }
  if (await changeOwnerAsync(wo.id, workOrder.value.ownerID)) {
    wo.ownerID = workOrder.value.ownerID
    serviceOrdersCardEl.value.loadDataAsync()
  }
}

const route = VueRouter.useRoute()
const router = VueRouter.useRouter()
const { fetchAuthAsync } = useAuth()
const unsavedChangesToCommissionWarehouse = ref(false)

const defaults = ref({ transportEndTime: null, timeEndTime: null })

const hasOpenWarehouseTransfers = computed(() => {
  return workOrder.value.warehouseTransfers?.filter((wt) => wt.status !== 7 && wt.status !== 9).length > 0 //InventoryUpdated = 7, Canceled = 9
})

const showFacilityHistory = ref(false)
const showUpload = ref(false)
const showAttachFromCamera = ref(false)
const showBarcode = ref(false)
const facility = ref(null)
const hasProject = ref(false)

const areLeakageCheckFormsOk = computed(() => {
  if (workOrder.value.isNew || devices.value.filter((d) => d.isLeakageTest).length === 0) {
    return true
  }
  return workOrder.value.leakageCheckForms.length >= devices.value?.filter((d) => d.isLeakageTest).length
})

const isBasedOnOffer = computed(() => {
  const offers = workOrder.value?.offers || []
  return offers.some((o) => o && !o.isPurchaseOrderRequest)
})

const hasPurchaseOrderRequest = computed(() => {
  const offers = workOrder.value?.offers || []
  return offers.some((o) => o && o.isPurchaseOrderRequest)
})

const markTriageAsync = async () => {
  await changeWorkOrderStatusAsync("Triage")
}

const markActiveAsync = async () => {
  if (await changeWorkOrderStatusAsync("Active")) {
    workOrderMaterialsEl.value?.reload()
  }
}

const markOfferAsync = async () => {
  if (await changeWorkOrderStatusAsync("Offer")) {
    workOrderMaterialsEl.value?.reload()
  }
}

const saveAndOpenNewLeakCheckFormAsync = async () => {
  await saveWorkOrderAsync()
  router.push({
    name: "ServiceOrderLeakageCheckForm",
    params: { workOrderId: workOrder.value.id, leakageCheckFormId: "new", keepMessages: true },
  })
}

const saveOfferAsync = async (requirePurchaseOrder = false) => {
  if (typeof requirePurchaseOrder !== "boolean") {
    console.error("assert failed: requirePurchaseOrder is not boolean", requirePurchaseOrder)
    return
  }
  if (await runOfferAsync(false, { isPurchaseOrderRequest: requirePurchaseOrder })) {
    await loadWorkOrderAsync(workOrder.value.id)
  }
}

const sendOfferAsync = async (offer) => {
  if (typeof offer.isPurchaseOrderRequest !== "boolean") {
    console.error("assert failed: offer.isPurchaseOrderRequest is not boolean", offer)
    return
  }
  if (
    await runOfferAsync(true, {
      offerId: offer.id,
      isPurchaseOrderRequest: offer.isPurchaseOrderRequest,
    })
  ) {
    await loadWorkOrderAsync(workOrder.value.id)
  }
}

const signDocumentAsync = (documentType, { recipientType, signatureName } = {}) => {
  if (!workOrder.value?.id || !documentType || !recipientType || !signatureName) {
    console.error("Missing parameters")
    return
  }

  const url = new URL("/sign/", window.location.origin)
  url.searchParams.set("documentType", documentType)
  url.searchParams.set("serviceOrderId", workOrder.value.id)
  url.searchParams.set("orderNumber", workOrder.value.orderNumber)
  url.searchParams.set("recipientType", recipientType)
  url.searchParams.set("signatureName", signatureName)
  url.searchParams.set("redirectTo", `${window.location.pathname}${window.location.search}${window.location.hash}`)
  window.location.assign(url.toString())
}

const saveAsNewAsync = async () => {
  // TODO: does this really work? saveAsNewWorkOrderAsync is basicly empty
  await saveAsNewWorkOrderAsync()
  unsavedChangesToCommissionWarehouse.value = false
}

const saveAsync = async () => {
  await saveWorkOrderAsync()
  unsavedChangesToCommissionWarehouse.value = false
  workOrderMaterialsEl.value?.reload()
}

const addDeviceToServiceOrder = (device) => {
  const result = devices.value.find((d) => d.id === device.id)
  if (result) {
    result.isRelated = true
  }
}

const handleFireWatchToggle = async () => {
  if (
    !workOrder.value.isFireWatch ||
    !facility.value?.id ||
    facility.value?.id === EMPTY_GUID ||
    facility.value?.id !== workOrder.value.facilityID
  ) {
    return
  }
  workOrder.value.fireWatch.level = facility.value.fireWatchLevel
  workOrder.value.fireWatch.requireFirefighters = facility.value.fireWatchLevel > 3
}

const handleHasProject = (newState) => {
  if (newState === false) {
    workOrder.value.projectID = EMPTY_GUID
  }
  hasProject.value = newState
}

const isValid = computed(() => {
  return (
    workOrder.value.subject?.length > 3 && !!workOrder.value.facilityID && workOrder.value.facilityID !== EMPTY_GUID
  )
})

const autoSavedWorkDescription = ref(null)
const remediationStatus = ref(0) // Default to "NE" (0)

const handleRemediationChange = (newValue) => {
  if (newValue === 2) {
    workOrder.value.dateRemediation = new Date().toISOString()
  } else {
    workOrder.value.dateRemediation = null
  }
}

const checkIfDateIsToday = (d) => {
  if (!d || typeof d.getMonth !== "function") {
    console.error("assert failed", d)
    return false
  }
  const today = new Date()
  return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate()
}

watch(
  workOrder,
  async () => {
    console.log("workOrder changed")
    if (!workOrder.value) {
      hasProject.value = null
      facility.value = null
      return
    }

    hasProject.value = !!workOrder.value.projectID && workOrder.value.projectID !== EMPTY_GUID
    if (hasProject.value) {
      workOrder.value.isSpecial = true
    }
    remediationStatus.value = workOrder.value.dateRemediation ? 2 : 0 // Set to "DA" (2) if dateRemediation exists

    if (workOrder.value.facilityID !== facility.value?.id) {
      facility.value = await getFacilityAsync(fetchAuthAsync, workOrder.value.facilityID, true)
    }

    if (workOrder.value?.id) {
      const autoSave = {
        workOrderId: workOrder.value.id,
        workDescription: workOrder.value.workDescription,
        savedOn: new Date().toISOString(),
      }
      localStorage.setItem("autoSave", JSON.stringify(autoSave))
    }
  },
  { deep: true },
)

onMounted(async () => {
  const autoSave = localStorage.getItem("autoSave")
  if (autoSave) {
    const parsed = JSON.parse(autoSave)
    if (parsed.workOrderId === route.params.workOrderId && checkIfDateIsToday(new Date(parsed.savedOn))) {
      autoSavedWorkDescription.value = parsed.workDescription
    }
  }
})

const setPlannedVisit = (day) => {
  workOrder.value.datePlannedVisitForInput = day.format("YYYY-MM-DD")
}
</script>
